import React, { FC, useContext, useEffect } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import { routes } from "@app/routing";
import {
  clearGisReturnUrl,
  getGisReturnUrl,
  isValidReturnUrl,
} from "@app/utils/gisEntrySource";
import Main from "./main";
import SetupProfile from "./setupProfile";
import {
  ChangePassword,
  CommunicationPreferences,
  ContactDetails,
  Home,
  PersonalInformation,
  Profile,
  ResendUpdateEmail,
  SampleDraw,
  SimpleOrder,
  TestHistory,
  Feedback,
  BillingUpdates,
  GuestLogin,
  BillingEstimate,
  OutOfNetwork,
  NonCoveredService,
  PriorAuthorization,
  TestResultPage,
  UpdateEmail,
  VideoPage,
  NoLongerAvailable,
  InsuranceNoLongerAvailable,
  ReviewBills,
  BillingPayment,
  BillingFailure,
  BillingConfirmation,
  BillingPaymentPlanDetail,
  BillingUpdatePayment,
  BillingPaymentSuccess,
  UPSUpdatePaymentMethodPage,
  UPSPaymentConfirmation,
  UPSReviewInvoice,
  UPSCreatePaymentMethod,
  UPSCreatePaymentConfirmation,
  UPSCreatePaymentFailure,
  UPSNoLongerAvailable,
  UPSReceipt,
} from "@app/pages";
import {
  NotificationContext,
  RedirectionContext,
  RoutingType,
} from "@app/provider";
import { RequireUpsSession } from "@app/ups/upsWrapper";
import PersonaToolingPanel from "@app/components/personaToolingPanel/personaToolingPanel";
import Confirmation from "./confirmation";
import SecuritySettings from "./securitySettings";
import UpdateEmailError from "@app/pages/public/updateEmailError";
import RenewalInterested from "@app/pages/public/renewalInterested";
import IntakeFormRouter from "@app/pages/private/scheduleAGeneticInformationSession/IntakeFormRouter";
import OncUppGisTriageLayout from "@app/pages/private/scheduleAGeneticInformationSession/GisIntakeSessionLayout";
import PublicGisTriage, {
  PublicGisOrderSelect,
} from "@app/pages/private/gisTriage";

import BillingHistoryStatements from "@app/postBill/pages/private/billingHistoryStatements";
import BillingHistoryPaymentPlans from "@app/postBill/pages/private/billingHistoryPaymentPlans";
import BillingHistoryPayments from "@app/postBill/pages/private/billingHistoryPayments";
import BillingCenterTab from "@app/postBill/pages/private/billingCenterTab";
import BillingReceiptPdf from "@app/postBill/pages/private/billingReceiptPdf";
import BillingStatementPdf from "@app/postBill/pages/private/billingStatementPdf";
import PatientEducationPage from "./scheduleAGeneticInformationSession/patientEducationPage";
import ScheduleSessionPage from "./scheduleAGeneticInformationSession/scheduleSessionPage";

/**
 * Default landing for any path that doesn't match a private route — most
 * notably the auth pages (`/log_in`, `/sign_up`, `/activation`) that the URL
 * still points at the moment authentication completes and private routing
 * takes over.
 *
 * Public-entry GIS visitors arrive here after logging in. `PublicGisEntry`
 * captures the desired post-auth destination both as a `returnUrl` query param
 * and in session storage (`gis_return_url`). We honor a valid return URL so the
 * patient lands back on the GIS triage page instead of being dumped on the
 * generic home/results page. The stored URL is cleared once consumed so a later
 * unmatched navigation can't bounce the user back into the GIS flow.
 */
const DefaultPrivateRedirect: FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const returnUrl = params.get("returnUrl") || getGisReturnUrl() || undefined;

  if (returnUrl && isValidReturnUrl(returnUrl)) {
    clearGisReturnUrl();
    return <Navigate to={returnUrl} replace />;
  }

  return <Navigate to={routes.home + location.search} replace />;
};

const PrivateRouting: FC = () => {
  const { notifications, clear } = useContext(NotificationContext);

  const location = useLocation();
  const navigationType = useNavigationType();

  const { setRedirection, redirectionTrigger, handleRedirection } =
    useContext(RedirectionContext);

  useEffect(() => {
    setRedirection(RoutingType.PRIVATE);
  }, []);

  useEffect(() => {
    if (redirectionTrigger) {
      handleRedirection();
    }
  }, [redirectionTrigger]);

  useEffect(() => {
    return () => {
      if (["PUSH", "REPLACE"].includes(navigationType)) {
        clear();
      }
    };
  }, [location, navigationType]);

  return (
    <Main>
      {notifications}
      <PersonaToolingPanel />
      <Routes>
        <Route path={routes.setupProfile} element={<SetupProfile />} />
        <Route path={routes.confirmation} element={<Confirmation />} />
        <Route path={routes.profile} element={<Profile />} />
        <Route path={routes.securitySettings} element={<SecuritySettings />} />
        <Route path={routes.home} element={<Home />} />
        <Route
          path={`${routes.contactDetails}/*`}
          element={<ContactDetails />}
        />
        <Route
          path={`${routes.personalInformation}/*`}
          element={<PersonalInformation />}
        />
        <Route
          path={`${routes.displayVideo}/:videoType`}
          element={<VideoPage />}
        />
        <Route path={routes.updateEmail} element={<UpdateEmail />} />
        <Route
          path={routes.resendUpdateEmail}
          element={<ResendUpdateEmail />}
        />
        <Route path={routes.changePassword} element={<ChangePassword />} />
        <Route path={`${routes.test}/*`} element={<TestResultPage />} />
        <Route path={`${routes.sampleDraw}/*`} element={<SampleDraw />} />
        <Route path={`${routes.simpleOrder}/*`} element={<SimpleOrder />} />
        <Route path={routes.testHistory} element={<TestHistory />} />
        <Route path={routes.feedback} element={<Feedback />} />
        <Route
          path={routes.renewalInterested}
          element={<RenewalInterested />}
        />
        <Route path={routes.billingCenter} element={<BillingCenterTab />} />
        <Route path={routes.reviewBills} element={<ReviewBills />} />
        <Route path={routes.billingPayment} element={<BillingPayment />} />
        <Route path={routes.billingFailure} element={<BillingFailure />} />
        <Route
          path={routes.billingConfirmation}
          element={<BillingConfirmation />}
        />
        <Route
          element={<BillingPaymentPlanDetail />}
          path={routes.billingPaymentPlanDetail}
        />
        <Route
          path={routes.billingPaymentSuccess}
          element={<BillingPaymentSuccess />}
        />
        {/* Redirect deprecated GIS page to home */}
        <Route
          path={routes.scheduleAGeneticInformationSession}
          element={<Navigate to={routes.home} replace />}
        />
        {/* Public-entry GIS triage: auto-routes the patient's order to either
            the ONC intake flow or the WH/OH Acuity iframe based on test type. */}
        <Route path={routes.gisTriage} element={<PublicGisTriage />} />
        {/* Multi-order picker: shown when a patient has more than one
            GIS-eligible order so they can choose which one to schedule. */}
        <Route
          path={routes.gisOrderSelect}
          element={<PublicGisOrderSelect />}
        />
        <Route element={<OncUppGisTriageLayout />}>
          <Route
            path={routes.scheduleAGeneticInformationSessionPatientEducation}
            element={<PatientEducationPage />}
          />
          <Route
            path={routes.scheduleAGeneticInformationSessionSchedule}
            element={<ScheduleSessionPage />}
          />
          <Route
            path={`${routes.scheduleAGeneticInformationSessionIntake}/*`}
            element={<IntakeFormRouter />}
          />
        </Route>
        <Route
          path={`${routes.communicationPreferences}/*`}
          element={<CommunicationPreferences />}
        />
        <Route path={routes.billingUpdates} element={<BillingUpdates />} />
        <Route path={routes.billingEstimate} element={<BillingEstimate />} />
        <Route
          path={routes.billingHistoryStatements}
          element={<BillingHistoryStatements />}
        />
        <Route
          path={routes.billingCenterPaymentPlanHistory}
          element={<BillingHistoryPaymentPlans />}
        />
        <Route
          path={routes.billingHistoryPayments}
          element={<BillingHistoryPayments />}
        />
        <Route
          path={routes.billingReceiptPdf}
          element={<BillingReceiptPdf />}
        />
        <Route
          path={routes.billingStatementPdf}
          element={<BillingStatementPdf />}
        />
        <Route
          path={routes.billingUpdatePayment}
          element={<BillingUpdatePayment />}
        />
        <Route
          element={<UPSUpdatePaymentMethodPage />}
          path={routes.upsUpdatePaymentMethod}
        />
        <Route
          element={<UPSPaymentConfirmation />}
          path={routes.upsUpdatePaymentMethodSuccess}
        />
        <Route element={<UPSReviewInvoice />} path={routes.upsReviewInvoice} />
        <Route
          element={
            <RequireUpsSession>
              <UPSCreatePaymentMethod />
            </RequireUpsSession>
          }
          path={routes.upsCreatePaymentMethod}
        />
        <Route
          element={<UPSCreatePaymentConfirmation />}
          path={routes.upsCreatePaymentConfirmation}
        />
        <Route
          element={<UPSCreatePaymentFailure />}
          path={routes.upsCreatePaymentFailure}
        />
        <Route
          element={<UPSNoLongerAvailable />}
          path={routes.upsNoLongerAvailable}
        />
        <Route element={<UPSReceipt />} path={routes.upsReceipt} />
        <Route path={routes.outOfNetwork} element={<OutOfNetwork />} />
        <Route
          path={routes.nonCoveredService}
          element={<NonCoveredService />}
        />
        <Route
          path={routes.priorAuthorization}
          element={<PriorAuthorization />}
        />
        <Route
          path={routes.noLongerAvailable}
          element={<NoLongerAvailable />}
        />
        <Route
          path={routes.insuranceNoLongerAvailable}
          element={<InsuranceNoLongerAvailable />}
        />
        <Route path={routes.guestLogin} element={<GuestLogin />} />
        <Route element={<UpdateEmailError />} path={routes.updateEmailError} />
        <Route path="*" element={<DefaultPrivateRedirect />} />
      </Routes>
    </Main>
  );
};

export default PrivateRouting;
